import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Wrench, AlertTriangle, PhoneCall, MapPin, PartyPopper,
         Sun, CloudSun, Cloud, CloudFog, CloudRain, CloudSnow, CloudLightning } from "lucide-react";

/**
 * Lo schermo appeso in capannone. Nessuno lo tocca mai: si aggiorna da solo e
 * sta tutto in una schermata, senza scorrimento e senza pagine che girano —
 * chi passa deve trovare la propria squadra al primo sguardo, non aspettare
 * il turno di una diapositiva.
 *
 * Le misure si stringono al crescere delle squadre: con quattro si legge da
 * lontano, con dodici si legge comunque, ma tutto resta in un solo schermo.
 */
const OGNI_MINUTO = 60 * 1000;

// Il meteo sta accanto all'indirizzo e resta piccolo: sul muro conta la
// squadra e dove va, il tempo e' un contorno. Se il cantiere non ha le
// coordinate salvate non compare niente, senza spazi vuoti né scritte.
const ICONA_METEO = {
  sereno: Sun, poco_nuvoloso: CloudSun, nuvoloso: Cloud, nebbia: CloudFog,
  pioggia: CloudRain, neve: CloudSnow, temporale: CloudLightning,
};

function Meteo({ dati }) {
  if (!dati) return null;
  const Icona = ICONA_METEO[dati.cielo] || Cloud;
  return (
    <span className="inline-flex items-center gap-1 text-slate-400 flex-shrink-0"
          title={dati.descrizione}>
      <Icona className="w-4 h-4" />
      <span className="tabular-nums">{dati.temperatura}°</span>
    </span>
  );
}

function scalaPer(n) {
  if (n <= 2) return { col: "grid-cols-2", squadra: "text-3xl", cantiere: "text-4xl",
                       riga: "text-2xl", nome: "text-3xl", pad: "p-5", gap: "gap-4" };
  if (n <= 4) return { col: "grid-cols-2", squadra: "text-2xl", cantiere: "text-3xl",
                       riga: "text-xl", nome: "text-2xl", pad: "p-4", gap: "gap-3" };
  if (n <= 6) return { col: "grid-cols-3", squadra: "text-xl", cantiere: "text-2xl",
                       riga: "text-lg", nome: "text-xl", pad: "p-3", gap: "gap-3" };
  if (n <= 9) return { col: "grid-cols-3", squadra: "text-lg", cantiere: "text-xl",
                       riga: "text-base", nome: "text-lg", pad: "p-3", gap: "gap-2" };
  return { col: "grid-cols-4", squadra: "text-base", cantiere: "text-lg",
           riga: "text-sm", nome: "text-base", pad: "p-2.5", gap: "gap-2" };
}

export default function Monitor() {
  const { logout } = useAuth();
  const nav = useNavigate();
  const [dati, setDati] = useState(null);
  const [errore, setErrore] = useState(false);
  const [orologio, setOrologio] = useState(new Date());
  const clicRef = useRef({ n: 0, t: null });

  // Uscita nascosta: tre clic sul logo. Un pulsante visibile su uno schermo in
  // capannone e' un invito a premerlo, ma senza alcuna uscita chi si collega
  // col monitor dal proprio computer resta chiuso fuori dal resto dell'app.
  const clicSulLogo = async () => {
    clearTimeout(clicRef.current.t);
    clicRef.current.n += 1;
    if (clicRef.current.n >= 3) {
      clicRef.current.n = 0;
      await logout();
      nav("/login");
      return;
    }
    clicRef.current.t = setTimeout(() => { clicRef.current.n = 0; }, 1500);
  };

  const carica = useCallback(async () => {
    try {
      const { data } = await api.get("/monitor/giornata");
      setDati(data); setErrore(false);
    } catch { setErrore(true); }
  }, []);

  useEffect(() => {
    carica();
    const t = setInterval(carica, OGNI_MINUTO);
    return () => clearInterval(t);
  }, [carica]);

  useEffect(() => {
    const t = setInterval(() => setOrologio(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const squadre = dati?.squadre || [];
  const s = useMemo(() => scalaPer(squadre.length), [squadre.length]);

  const ora = orologio.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
  const aggiornato = dati?.aggiornato_alle
    ? new Date(dati.aggiornato_alle).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })
    : "—";

  return (
    <div className="h-screen w-screen overflow-hidden bg-slate-950 text-slate-100
                    px-4 py-3 flex flex-col gap-3"
         data-testid="monitor-page">

      <header className="flex items-center justify-between gap-6 flex-shrink-0">
        <div className="min-w-0 flex items-baseline gap-4">
          <div className="text-4xl font-black leading-none truncate">
            {dati?.etichetta || "Caricamento…"}
          </div>
          {dati?.giorno_saltato && (
            <div className="text-amber-400 text-base flex-shrink-0">
              nessun lavoro prima di questa data
            </div>
          )}
          <div className="text-slate-500 text-base flex-shrink-0">
            {squadre.length} squadr{squadre.length === 1 ? "a" : "e"}
          </div>
        </div>
        <div className="flex items-center gap-4 flex-shrink-0">
          <div className="text-right leading-none">
            <div className="text-4xl font-black tabular-nums">{ora}</div>
            <div className={`text-xs ${errore ? "text-red-400" : "text-slate-600"}`}>
              {errore ? "collegamento interrotto" : `aggiornato ${aggiornato}`}
            </div>
          </div>
          <img src="/assets/sgm-logo.svg" alt="SGM Impianti"
               onClick={clicSulLogo} data-testid="monitor-logo"
               className="h-10 w-auto brightness-0 invert select-none" />
        </div>
      </header>

      {/* Gli auguri stanno sopra le squadre, dove l'occhio va per primo, e
          ci stanno solo il giorno del compleanno: nove giorni su dieci questa
          fascia non c'è e lo schermo è quello di sempre. */}
      {(dati?.compleanni || []).length > 0 && (
        <div className="flex-shrink-0 rounded-xl bg-gradient-to-r from-orange-600 to-amber-500
                        px-4 py-2 flex items-center gap-4 flex-wrap"
             data-testid="monitor-compleanni">
          <PartyPopper className="w-8 h-8 text-white flex-shrink-0" />
          <div className="flex-1 min-w-0 flex items-center gap-6 flex-wrap">
            {dati.compleanni.map((c) => (
              <span key={c.id} className="text-white font-black text-2xl leading-tight">
                {c.auguri}
              </span>
            ))}
          </div>
        </div>
      )}

      <main className={`flex-1 min-h-0 grid ${s.col} ${s.gap} auto-rows-fr`}>
        {squadre.map((sq, i) => (
          <section key={`${sq.squadra}-${i}`}
                   className={`bg-slate-900 border border-slate-800 rounded-xl ${s.pad}
                               min-h-0 overflow-hidden flex flex-col`}
                   data-testid={`monitor-squadra-${i}`}>
            <div className={`text-orange-500 ${s.squadra} font-black uppercase leading-none
                             flex-shrink-0`}>
              {sq.squadra}
            </div>

            {sq.tappe.map((t, j) => (
              <div key={j} className={`flex-shrink-0 ${j > 0 ? "mt-1.5 pt-1.5 border-t border-slate-800" : "mt-1.5"}`}>
                <div className={`${s.cantiere} font-bold leading-tight`}>{t.cantiere}</div>
                <div className={`${s.riga} leading-tight`}>
                  {t.cliente && <span className="text-sky-400">{t.cliente}</span>}
                  {t.cliente && t.attivita && <span className="text-slate-600"> · </span>}
                  {t.attivita && <span className="text-slate-400">{t.attivita}</span>}
                </div>
                {t.indirizzo && (
                  <div className={`${s.riga} text-slate-300 leading-tight flex items-start gap-1`}>
                    <MapPin className="w-4 h-4 mt-1 flex-shrink-0 text-slate-600" />
                    <span className="flex-1">{t.indirizzo}{t.comune ? ` · ${t.comune}` : ""}</span>
                    <span className="text-sm mt-1"><Meteo dati={t.meteo} /></span>
                  </div>
                )}
                {!t.indirizzo && t.meteo && (
                  <div className="text-sm leading-tight mt-0.5"><Meteo dati={t.meteo} /></div>
                )}
              </div>
            ))}

            <div className="mt-auto pt-2 min-h-0">
              <div className={`${s.nome} leading-tight flex flex-wrap gap-x-3`}>
                {sq.persone.length === 0
                  ? <span className="text-slate-600">nessuno assegnato</span>
                  : sq.persone.map((p, k) => <span key={k}>{p.full_name}</span>)}
              </div>
              {sq.mezzi.length > 0 && (
                <div className={`${s.riga} text-slate-400 leading-tight flex flex-wrap gap-x-3 mt-1`}>
                  {sq.mezzi.map((m, k) => (
                    <span key={k} className="text-amber-300">{m.identifier}</span>
                  ))}
                </div>
              )}
            </div>
          </section>
        ))}

        {dati && squadre.length === 0 && (
          <div className="col-span-full flex items-center justify-center text-4xl text-slate-700">
            Nessuna squadra programmata
          </div>
        )}
      </main>

      <footer className="flex-shrink-0 border-t border-slate-800 pt-2 flex items-start
                         justify-between gap-6 text-sm">
        <div className="flex items-center gap-5 flex-wrap min-w-0">
          <span className="text-slate-600 uppercase tracking-widest flex items-center gap-1 flex-shrink-0">
            <PhoneCall className="w-4 h-4" /> Reperibili
          </span>
          {(dati?.reperibili || []).map(r => (
            <span key={r.codice} className="flex items-baseline gap-1.5 text-base">
              <span className="text-slate-500">{r.nome}</span>
              <span className="text-sky-400 font-bold tabular-nums">{r.telefono_turno}</span>
              <span className="text-slate-300">{r.in_turno?.full_name || "—"}</span>
            </span>
          ))}
        </div>

        <div className="flex items-start gap-5 flex-shrink-0 max-w-[45%]">
          {(dati?.mezzi_fermi || []).length > 0 && (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-slate-600 uppercase tracking-widest flex items-center gap-1">
                <Wrench className="w-4 h-4" /> Fermi
              </span>
              {dati.mezzi_fermi.map((m, i) => (
                <span key={i} className="text-base text-slate-300">{m.identifier}</span>
              ))}
            </div>
          )}
          {(dati?.segnalazioni || []).slice(0, 2).map((sg, i) => (
            <span key={i} className={`text-base leading-tight truncate ${
              sg.gravita === "alta" ? "text-red-400" : "text-amber-400"}`}>
              <AlertTriangle className="w-4 h-4 inline mb-0.5" />{" "}
              <strong>{sg.dove}</strong> · {sg.descrizione}
            </span>
          ))}
        </div>
      </footer>
    </div>
  );
}
